// src/pages/Lounge/MyClubs/MyClubs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "@/utils/axios";
import Loading from "@/components/Loading/Loading";
import cls from "./MyClubs.module.scss";

import avatarPng from "@/assets/UI/avatar.png";

const safeImg = (v) => (v ? String(v) : "");
const pickClubThumb = (club) => {
  // backend returns: clubAvatarThumb: { key, url }
  const url = club?.clubAvatarThumb?.url || club?.clubAvatarThumbUrl || "";
  return safeImg(url);
};

const MyClubs = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [partnerClubs, setPartnerClubs] = useState([]);
  const [hostClubs, setHostClubs] = useState([]);
  const [memberClubs, setMemberClubs] = useState([]);

  const total = useMemo(
    () => partnerClubs.length + hostClubs.length + memberClubs.length,
    [partnerClubs.length, hostClubs.length, memberClubs.length],
  );

  useEffect(() => {
    let live = true;

    const run = async () => {
      try {
        setLoading(true);
        setErr("");

        // Adjust this path if your route differs
        const { data } = await api.get("/v1/lounge/my-clubs");

        if (!live) return;

        setPartnerClubs(
          Array.isArray(data?.partnerClubs) ? data.partnerClubs : [],
        );
        setHostClubs(Array.isArray(data?.hostClubs) ? data.hostClubs : []);
        setMemberClubs(
          Array.isArray(data?.memberClubs) ? data.memberClubs : [],
        );
      } catch (e) {
        if (!live) return;
        setErr(
          e?.response?.data?.message || e?.message || "Something went wrong",
        );
      } finally {
        if (live) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      live = false;
    };
  }, []);

  const ClubList = ({ title, subtitle, rows, toneClass }) => {
    const count = rows?.length || 0;

    return (
      <section className={cls.postCard}>
        <div className={cls.sectionHead}>
          <div className={cls.sectionTitleWrap}>
            <h3 className={cls.sectionTitle}>{title}</h3>
            <span className={`${cls.countPill} ${toneClass || ""}`}>
              {count}
            </span>
          </div>
          {subtitle ? <div className={cls.sectionSub}>{subtitle}</div> : null}
        </div>

        {count === 0 ? (
          <div className={cls.emptyBox}>{t("myClubs.empty")}</div>
        ) : (
          <div className={cls.clubsGrid}>
            {rows.map((c) => {
              const clubId = c?.clubId;
              const img = pickClubThumb(c);
              const clubNo = c?.clubNo ?? "";
              const clubName = c?.clubName || "-";
              const cityName = c?.cityName || "-";
              const streetName = c?.streetName || "-";

              return (
                <article key={clubId} className={cls.clubItem}>
                  <div className={cls.clubTop}>
                    <img
                      src={img || avatarPng}
                      alt={t("myClubs.avatarAlt")}
                      className={cls.clubAvatar}
                      onError={(ev) => {
                        ev.currentTarget.src = avatarPng;
                      }}
                      loading="lazy"
                    />

                    <div className={cls.clubInfo}>
                      <div className={cls.clubNameRow}>
                        <div className={cls.clubName}>{clubName}</div>
                        {clubNo ? (
                          <span className={cls.noPill}>
                            {t("myClubs.clubNo")} {clubNo}
                          </span>
                        ) : null}
                      </div>

                      <div className={cls.clubMetaLine}>
                        <span className={cls.addressLine}>
                          {clubNo ? `${clubNo}, ` : ""}
                          {streetName}
                          {streetName && cityName ? ", " : ""}
                          {cityName}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={cls.clubBottom}>
                    <Link
                      to={`/lounge/clubs/single/${clubId}`}
                      className={cls.visitBtn}
                    >
                      {t("myClubs.visit")}
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        {/* Main layout – single centered column */}
        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA – compact 3-button row */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                {/* My Clubs */}
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 
          1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 
          3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 
          2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 
          14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 
          1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z"
                      />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                {/* Explore */}
                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M12 2C6.48 2 2 6.48 2 
          12s4.48 10 10 10 10-4.48 
          10-10S17.52 2 12 
          2zm4.19 6.19l-2.47 7.4c-.1.31-.49.31-.59 
          0l-2.47-7.4a.5.5 0 01.63-.63l7.4 2.47c.31.1.31.49 
          0 .59l-7.4 2.47a.5.5 0 01-.63-.63l2.47-7.4c.1-.31.49-.31.59 
          0l2.47 7.4a.5.5 0 01-.63.63l-7.4-2.47c-.31-.1-.31-.49 
          0-.59l7.4-2.47a.5.5 0 01.63.63z"
                      />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                {/* Search */}
                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M15.5 14h-.79l-.28-.27A6.471 
          6.471 0 0016 9.5 6.5 6.5 
          0 109.5 16a6.471 6.471 0 004.23-1.57l.27.28v.79l5 
          5L20.49 19l-5-5zm-6 
          0C7.01 14 5 11.99 5 9.5S7.01 
          5 9.5 5 14 7.01 14 9.5 11.99 
          14 9.5 14z"
                      />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* Replaced: Posts feed (club posts)  -> Club details */}
            <section className={cls.postCard}>
              <div className={cls.cardHeaderRow}>
                <h3 className={cls.cardTitle}>{t("myClubs.title")}</h3>
                <div className={cls.cardMeta}>
                  {loading
                    ? t("myClubs.loading")
                    : `${t("myClubs.total")}: ${total}`}
                </div>
              </div>

              {err ? (
                <div className={cls.errBox}>
                  {t("myClubs.error")}: {err}
                </div>
              ) : null}
              {loading ? (
                <div className={cls.loadingBox}>
                  <Loading />
                </div>
              ) : null}
            </section>

            {!loading && !err ? (
              <>
                {partnerClubs.length > 0 ? (
                  <ClubList
                    title={t("myClubs.sections.partner")}
                    subtitle={t("myClubs.sections.partnerSub")}
                    rows={partnerClubs}
                    toneClass={cls.pillPartner}
                  />
                ) : null}
                {hostClubs.length > 0 ? (
                  <ClubList
                    title={t("myClubs.sections.host")}
                    subtitle={t("myClubs.sections.hostSub")}
                    rows={hostClubs}
                    toneClass={cls.pillHost}
                  />
                ) : null}
                {memberClubs.length > 0 ? (
                  <ClubList
                    title={t("myClubs.sections.member")}
                    subtitle={t("myClubs.sections.memberSub")}
                    rows={memberClubs}
                    toneClass={cls.pillMember}
                  />
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyClubs;
